import axios from "axios";
import store from "@/state/store";
import alert from "@/_helpers/alert";

import router from "@/router";
import userData from "@/plugins/userData";

const api = axios.create({
    baseURL: process.env.VUE_APP_ENDPOINT_BACKEND + "/client",
    headers: {
        "Content-Type": "application/json",
    },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // ตรวจสอบว่า data เป็น FormData หรือไม่
    if (config.data instanceof FormData) {
        // กำหนด Content-Type เป็น multipart/form-data โดยไม่ระบุ boundary
        // Axios จะจัดการเพิ่ม boundary ให้อัตโนมัติ
        config.headers["Content-Type"] = "multipart/form-data";
    }

    return config;
});

api.interceptors.response.use(
    (response) => {
        const contentType = response.headers?.["content-type"];
        const content = response.headers?.["content-disposition"];

        if (contentType.includes("json")) {
            return response.data;
        } else if (
            contentType.includes(
                "vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            )
        ) {
            return {
                blob: response.data,
                filename: content
                    ? content.match(/filename="(.+)"/)[1]
                    : `file_${Date.now()}.xlsx`,
            };
        } else if (contentType.includes("zip")) {
            return {
                blob: response.data,
                filename: content
                    ? content.match(/filename="(.+)"/)[1]
                    : `file_${Date.now()}.zip`,
            };
        } else if (contentType.includes("pdf")) {
            return {
                blob: response.data,
                filename: `file_${Date.now()}.pdf`,
            };
        } else if (contentType.includes("csv")) {
            return {
                blob: "\uFEFF" + response.data, // เพิ่มการรองรับ utf-8
                filename: `file_${Date.now()}.csv`,
            };
        }

        return response.data;
    },
    async (error) => {
        // ตรวจสอบว่า responseType เป็น blob และเกิด error
        if (error.config?.responseType === 'blob' && error.response?.data instanceof Blob) {
            // ตรวจสอบ content-type ของ error response
            const contentType = error.response.headers?.['content-type'] || '';
            
            // ถ้า error response เป็น JSON ให้แปลง blob เป็น JSON
            if (contentType.includes('application/json')) {
                try {
                    const text = await error.response.data.text();
                    const json = JSON.parse(text);
                    // แทนที่ error.response.data ด้วย JSON object
                    error.response.data = json;
                } catch (parseError) {
                    console.error('Failed to parse error blob as JSON:', parseError);
                }
            }
        }

        const handleError = {
            data: error.response,
            error: error?.response?.data,
        };

        if (error.response) {
            const statusCode = parseInt(error.response.status, 10);

            switch (statusCode) {
                case 401:
                    alert.error(
                        "401 เซสชั่นของคุณหมดอายุ กรุณาล็อคอินอีกครั้ง"
                    );

                    // set accountId สำหรับเลือกอัตโนมัติตอน login กลับมา
                    localStorage.setItem("redirectAccountId", userData.getParent()?.id);
                    // พาไป logout เคลียร์ข้อมูล
                    router.push({
                        path: "/user/logout",
                    });
                    return {
                        error: {
                            message: handleError.data.data,
                        },
                    };
                case 403:
                    alert.error("403 คุณไม่มีสิทธิในการเข้าถึง");
                    return {
                        error: {
                            message: handleError.data.data,
                        },
                    };
                case 404:
                    alert.error("404 ไม่พบข้อมูล");
                    return {
                        error: {
                            message: handleError.data.data,
                        },
                    };
                default:
                    alert.error(`${statusCode} เกิดข้อผิดพลาดภายในระบบ`);
                    return {
                        error: {
                            message: handleError.data.data,
                        },
                    };
            }
        }

        alert.error("ไม่สามารถดึงข้อมูลได้");
        return {
            error: {
                message: handleError.data.data,
            },
        };
    }
);

export const actions = {
    //ANCHOR: Packaging
    async getPackaging({}) {
        return await api.get(`/v1/packagings`);
    },

    //ANCHOR: Shipment Orders
    /**
     * @param {Object} data
     * @param {number} data.page
     * @param {number} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     * @param {string} data.claim_flag
     * @param {string} data.cod_flag
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.open_api
     * @param {string} data.partner_id
     */
    async getShipmentOrdersSummary({}, data) {
        return await api.get(`/v2/shipment-orders/summary`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {Array<number>} data.id
     * @param {string} data.size
     */
    async getShipmentProofOfShipmentDocs({}, data) {
        return await api.post(
            `/v2/shipment-orders/proof-of-shipment-docs`,
            data,
            { responseType: "blob" }
        );
    },

    /**
     * @param {Object} data
     * @param {Array<number>} data.date_type
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async exportShipmentOrders({}, data) {
        return await api.get(`/v2/shipment-orders/export`, {
            params: data,
            responseType: "blob",
        });
    },

    /**
     * @param {number} id
     */
    async getShipmentLogs({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/shipment-logs`);
    },

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.cod_flag
     * @param {string} data.partner_awb_no
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     */
    async getShipmentOrders({}, data) {
        return await api.get(`/v2/shipment-orders`, { params: data });
    },

    /**
     * @param {number} id
     */
    async getShipmentOrderById({}, id) {
        return await api.get(`/v2/shipment-orders/${id}`);
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {string} data.remark
     */
    async cancelShipmentOrder({}, data) {
        return await api.patch(`/v1/shipment-orders/${data.id}/cancel`, {
            remark: data.remark,
        });
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {Object} data.formData
     */
    async postShipmentOrdersClaims({}, data) {
        return await api.post(
            `/v1/shipment-orders/${data.id}/claims`,
            data.formData
        );
    },

    /**
     * @param {number} id
     */
    async getDocumentCreditNote({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/documents`);
    },

    /**
     * @param {number} id
     */
    async getReversal({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/reversal`);
    },

    /**
     * @param {number} id
     */
    async getShipmentOrderItems({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/shipment-order-items`);
    },

    /**
     * @param {Object} data
     * @param {Array<Object>}  data.orders
     * @param {string} data.orders.partner
     * @param {number} data.orders.insurance_amount
     */
    async getInsurancePrice({}, data) {
        return await api.post(
            `/v2/shipment-orders/partner-insurance-prices`,
            data
        );
    },

    /**
     * @param {Object} data
     * @param {Array<Object>} data.orders
     * @param {number} data.orders.pickup_point_id
     * @param {Object} data.orders.to
     * @param {string} data.orders.to.district
     * @param {string} data.orders.to.city
     * @param {string} data.orders.to.province
     * @param {string} data.orders.to.postal_code
     * @param {Object} data.orders.parcel
     * @param {number} data.orders.parcel.width
     * @param {number} data.orders.parcel.height
     * @param {number} data.orders.parcel.length
     * @param {number} data.orders.parcel.weight
     * @param {string} data.orders.cod_amount
     */
    async getEstimateShippingPrice({}, data) {
        return await api.post(
            `/v2/shipment-orders/partner-shipping-prices`,
            data
        );
    },

    /**
     * @param {Object} data
     * @param {Object} data.from
     * @param {string} data.from.name
     * @param {string} data.from.address
     * @param {string} data.from.district
     * @param {string} data.from.city
     * @param {string} data.from.province
     * @param {string} data.from.postal_code
     * @param {string} data.from.phone_number
     * @param {Object} data.to
     * @param {string} data.to.name
     * @param {string} data.to.address
     * @param {string} data.to.district
     * @param {string} data.to.city
     * @param {string} data.to.province
     * @param {string} data.to.postal_code
     * @param {string} data.to.phone_number
     * @param {Object} data.parcel
     * @param {string} data.parcel.package_name
     * @param {number} data.parcel.width
     * @param {number} data.parcel.height
     * @param {number} data.parcel.length
     * @param {number} data.parcel.weight
     * @param {Array<Object>} data.parcel.items
     * @param {string} data.parcel.items[].name
     * @param {number} data.parcel.items[].qty
     * @param {number} data.parcel.items[].price
     * @param {number} data.partner_id
     * @param {number} data.pickup_point_id
     * @param {string} data.label_remark
     * @param {number} data.insurance_amount
     * @param {string} data.channel
     */
    async createOrder({}, data) {
        return await api.post(`/v1/shipment-orders`, data);
    },

    /**
     * @param {Array<Object>} data
     * @param {Object} data[].from
     * @param {string} data[].from.name
     * @param {string} data[].from.address
     * @param {string} data[].from.district
     * @param {string} data[].from.city
     * @param {string} data[].from.province
     * @param {string} data[].from.postal_code
     * @param {string} data[].from.phone_number
     * @param {Object} data[].to
     * @param {string} data[].to.name
     * @param {string} data[].to.address
     * @param {string} data[].to.district
     * @param {string} data[].to.city
     * @param {string} data[].to.province
     * @param {string} data[].to.postal_code
     * @param {string} data[].to.phone_number
     * @param {Object} data[].parcel
     * @param {string} data[].parcel.package_name
     * @param {number} data[].parcel.width
     * @param {number} data[].parcel.height
     * @param {number} data[].parcel.length
     * @param {number} data[].parcel.weight
     * @param {Array<Object>} data[].parcel.items
     * @param {string} data[].parcel.items[].name
     * @param {number} data[].parcel.items[].qty
     * @param {number} data[].parcel.items[].price
     * @param {number} data[].partner_id
     * @param {number} data[].pickup_point_id
     * @param {string} data[].label_remark
     * @param {number} data[].insurance_amount
     * @param {string} data[].channel
     */
    async createMultipleOrder({}, data) {
        return await api.post(`/v1/shipment-orders/bulk-create`, data);
    },

    /**
     * @param {Object} data
     */
    async getShipmentOrdersCod({}, data) {
        return await api.get(`/v2/reports/shipment-orders/cod`, {
            params: data,
        });
    },

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.date_type
     */
    async getShipmentOrdersCodSummary({}, data) {
        return await api.get(`/v2/reports/shipment-orders/cod/summary`, {
            params: data,
        });
    },

    /**
     * @param {Object} data
     */
    async getShipmentOrdersCodSummaryAmount({}, data) {
        return await api.get(`/v1/reports/shipment-orders/cod/summary-amount`, {
            params: data,
        });
    },

    /**
     * @param {number} id
     */
    async getHistoryInsuranceOrder({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/wallet-service-transactions`);
    },

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getShipmentOrdersDaily({}, data) {
        return await api.get(`/v1/reports/shipment-orders/daily`, {
            params: data,
        });
    },

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.date_type
     * @param {string} data.status
     * @param {string} data.search
     */
    async exportShipmentOrdersCod({}, data) {
        return await api.get(`/v2/reports/shipment-orders/cod/export`, {
            params: data,
            responseType: "blob",
        });
    },

    /**
     * @param {number} id
     */
    async getWalletTransaction({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/wallet-transactions`);
    },

    /**
     * @param {number} id
     */
    async getShipmentOrdersClaims({}, id) {
        return await api.get(`/v1/shipment-orders/${id}/claims`);
    },

    //ANCHOR: Partner
    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {number} data.active_flag
     */
    async getPartners({}, data) {
        return await api.get(`/v1/partners`, { params: data });
    },

    async getPartnerRate({}) {
        return await api.get(`/v1/partner-rates`);
    },

    //ANCHOR: Pickup Doc
    /**
     * @param {Object} data
     * @param {string} data.pickup_dt
     * @param {string} data.page
     * @param {string} data.limit
     */
    async getPickupDoc({}, data) {
        return await api.get(`/v1/pickup-doc`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {string} data.pickup_dt
     */
    async getPickupDocSummary({}, data) {
        return await api.get(`/v1/pickup-doc/summary`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {string} data.partner
     * @param {Object} data.formData
     */
    async postPickupDoc({}, data) {
        return await api.post(`/v1/pickup-doc/${data.partner}`, data.formData);
    },

    /**
     * @param {string} data
     */
    async patchPickupDocFlash({}, data) {
        return await api.patch(`/v1/pickup-doc/flash`, { pickup_dt: data });
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {string} data.pickup_dt
     * @param {string} data.limit
     * @param {string} data.page
     * @param {string} data.search
     * @param {string} data.status
     * @param {string} data.partner_id
     */
    async getPickupById({}, data) {
        return await api.get(`/v1/pickup-doc/${data.id}`);
    },

    //ANCHOR: Receiver
    async getReceiveAddress({}) {
        return await api.get(`/account-api/v1/receivers`);
    },

    //ANCHOR: Claim Type
    async getClaimType({}) {
        return await api.get(`/v1/claims/types`);
    },

    //ANCHOR: Commission Order
    /**
     * @param {number} id
     */
    async getCommissionOrderApiLogs({}, id) {
        return await api.get(`/admin/v1/commission-orders/${id}/api-logs`);
    },

    /**
     * @param {Object} data
     * @param {string} data.search
     * @param {string} data.page
     * @param {string} data.limit
     */
    async getCustomers({}, data) {
        return await api.get(`/v1/customers`, { params: data });
    },

    /**
     * @param {number} id
     */
    async deleteCustomer({}, id) {
        return await api.delete(`/v1/customers/${id}`);
    },

    //ANCHOR: Auth
    /**
     * @param {string} data
     * @param {string} data.telephone
     */
    async requestOtpForForgotPassword({}, data) {
        return await api.post(`/v1/auth/forgot-password`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.telephone
     * @param {string} data.ref
     * @param {string} data.otp
     */
    async verifyOtp({}, data) {
        return await api.post(`/v1/auth/verify-otp`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.telephone
     * @param {string} data.ref
     * @param {string} data.otp
     * @param {string} data.new_password
     * @param {string} data.new_password_confirmation
     */
    async resetPassword({}, data) {
        return await api.post(`/v1/auth/forgot-password-confirm`, data);
    },

    //ANCHOR: Logout
    async postLogout({}) {
        return await api.post(`/v1/auth/logout`);
    },

    //ANCHOR: Account Child Link
    /**
     * @param {Object} data
     * @param {string} data.search
     * @param {string} data.page
     * @param {string} data.limit
     */
    async getAccountChildLink({}, data) {
        return await api.get(`/v1/account-child-link`, { params: data });
    },
    /**
     * @param {object} data
     * @param {number} data.parent_id
     */
    async postAccountChildLink({}, data) {
        return await api.post(`/v1/account-child-link`, data);
    },

    //ANCHOR: Account Access Levels
    async getAccountAccessLevels({}) {
        return await api.get(`/v1/account-access-levels`);
    },

    //ANCHOR: Invite
    /**
     * @param {string} key
     */
    async checkInviteKey({}, key) {
        return await api.get(`/v1/invite/${key}`);
    },

    //ANCHOR: Dashboard

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getDashboardShipmentOrdersDaily({}, data) {
        return await api.get(`/v1/dashboards/daily-shipment-orders`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getDashboardShipmentOrdersProvinces({}, data) {
        return await api.get(
            `/v1/dashboards/period-shipment-orders/provinces`,
            { params: data }
        );
    },

    /**
     * @param {Object} data
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getDashboardShipmentOrdersVolumes({}, data) {
        return await api.get(`/v1/dashboards/period-shipment-orders/volumes`, {
            params: data,
        });
    },

    //ANCHOR: Login
    /**
     * @param {Object} data
     * @param {string} data.username
     * @param {string} data.password
     */
    async login({}, data) {
        return await api.post(`/v1/auth/login`, data);
    },

    //ANCHOR: Register
    /**
     * @param {Object} data
     * @param {string} data.telephone
     */
    async requestOtpForRegister({}, data) {
        return await api.post(`/v1/register/request-otp-for-register`, data);
    },

    /**
     * @param {Object} data
     */
    async postConfirmOtp({}, data) {
        return await api.post(`/v1/register/confirm-otp`, data);
    },

    //ANCHOR: accounts child
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     */
    async getAccountsChild({}, data) {
        return await api.get(`/v1/accounts-childs`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {number} data.access_level
     * @param {number} data.active_flag
     */
    async patchAccountsChild({}, data) {
        return await api.patch(`/v1/accounts-childs/${data.id}`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.access_level
     */
    async postGenerateInviteAccountsChild({}, data) {
        return await api.post(`/v1/accounts-childs/generate-invite`, data);
    },

    /**
     * @param {number} id
     */
    async deleteAccountsChild({}, id) {
        return await api.delete(`/v1/accounts-childs/${id}`);
    },
    /**
     * @param {string} key
     */
    async acceptInvite({}, key) {
        return await api.post(`/v1/accounts-childs/verify/${key}`);
    },

    //ANCHOR: Accounts Request Support
    /**
     * @param {boolean} statusRequest
     */
    async patchAccountsRequestSupport({}, statusRequest) {
        return await api.patch(`/v1/accounts/request-support`, {
            request_support_flag: statusRequest,
        });
    },

    //ANCHOR: Notifications
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.priority
     */
    async getNotifications({}, data) {
        return await api.get("/v1/notifications", { params: data });
    },
    /**
     * @param {number} id
     */
    async patchNotificationRead({}, id) {
        return await api.patch(`/v1/notifications/${id}/read`);
    },

    //ANCHOR: Accounts
    /**
     * @param {Object} data
     */
    async getAccounts({}, data) {
        return await api.get("/v1/accounts", { params: data });
    },

    /**
     * @param {Object} email
     */
    async getverifyEmail({}, email) {
        return await api.get("/v1/accounts/email-verify", { params: email });
    },

    /**
     * @param {Object} data
     * @param {string} data.name
     * @param {number} data.recalculate_threshold
     * @param {number} data.pickup_shipping_fee_threshold
     * @param {number} data.order_cancellation_period
     * @param {number} data.address_correction_flag
     * @param {Array<string>} data.document_email_list
     */
    async patchAccountsProfile({}, data) {
        return await api.patch(`/v1/accounts/profile`, data);
    },

    /**
     * @param {Object} data
     * @param {Object} data.invoice
     * @param {string} data.invoice.address
     * @param {string} data.invoice.name
     * @param {string} data.invoice.tel
     * @param {string} data.invoice.branch
     * @param {string} data.invoice.tax_id
     * @param {string} data.email
     * @param {string} data.juristic_card_verify_img
     * @param {string} data.certificate_verify_img
     * @param {string} data.vat_registration_verify_img
     */
    async patchAccountsJuristicVerify({}, data) {
        return await api.patch(`/v1/accounts/juristic-verify`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.bank_acc_name
     * @param {string} data.bank_name
     * @param {string} data.bank_code
     * @param {string} data.bank_acc_no
     * @param {string} data.bank_verify_img
     * @param {string} data.email
     */
    async patchAccountsCodVerify({}, data) {
        return await api.patch(`/v1/accounts/cod-verify`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.invoice.first_name
     * @param {string} data.invoice.last_name
     * @param {string} data.invoice.tel
     * @param {string} data.invoice.address
     * @param {string} data.card_id
     * @param {string} data.card_verify_img
     * @param {string} data.email
     */
    async patchAccountsCardVerify({}, data) {
        return await api.patch(`/v1/accounts/card-verify`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.email
     * @param {string} data.otp
     * @param {string} data.ref
     */
    async postAccountsVerifyEmailOtp({}, data) {
        return await api.post(`/v1/accounts/verify-email-otp`, data);
    },


    /**
     * @param {Object} data
     * @param {string} data.telephone
     * @param {string} data.otp
     * @param {string} data.ref
     * @param {string} data.telephone
     */
      async deleteAccounts({}, data) {
        return await api.delete(`/v1/accounts`, {data: data});
    },

    //ANCHOR: Pickup Points
    /**
     * @param {Object} data
     * @param {string} data.page
     */
    async getPickupPoints({}, data) {
        return await api.get(`/v1/pickup-points`, { params: data });
    },

    async getPickupPointConfigs({}, data) {
        return await api.get(`/v1/pickup-point-configs`);
    },

    /**
     * @param {Object} data
     * @param {number} data.partner_id
     * @param {Array<string>} data.days
     */
    async patchPickupPointConfigs({}, data) {
        return await api.patch(`/v1/pickup-point-configs`, data);
    },

    /**
     * @param {Object} data
     * @param {string} data.pickup_point_name
     * @param {string} data.pickup_point_addr
     * @param {string} data.pickup_point_district
     * @param {string} data.pickup_point_city
     * @param {string} data.pickup_point_province
     * @param {string} data.pickup_point_postal_code
     * @param {string} data.responser_name
     * @param {string} data.responser_tel
     * @param {string} data.default_flag
     */
    async createPickupPoint({}, data) {
        return await api.post(`/v1/pickup-points`, data);
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {string} data.pickup_point_name
     * @param {string} data.pickup_point_addr
     * @param {string} data.pickup_point_district
     * @param {string} data.pickup_point_city
     * @param {string} data.pickup_point_province
     * @param {string} data.pickup_point_postal_code
     * @param {string} data.responser_name
     * @param {string} data.responser_tel
     * @param {string} data.default_flag
     */

    async editPickupPoint({}, data) {
        return await api.patch(`/v1/pickup-points/${data.id}`, data);
    },

    /**
     * @param {number} id
     */
    async deletePickupPoint({}, id) {
        return await api.delete(`/v1/pickup-points/${id}`);
    },

    //ANCHOR: Label
    /**
     * @param {Object} data
     * @param {string} data.id
     * @param {string} data.size
     * @param {string} data.print_item
     */
    async getLabel({}, data) {
        return await api.get(`/v1/label`, {
            params: data,
            responseType: "blob",
        });
    },

    //ANCHOR: Init
    async getInit({}) {
        return await api.get(`/v1/init`);
    },

    //ANCHOR: Banner
    /**
     * @param {Object} data
     * @param {string} data.status
     */
    async getBanner({}, data) {
        return await api.get(`/v1/carousel-banner`, { params: data });
    },

    //ANCHOR: Topup
    /**
     * @param {Object} data
     */
    async getTopupPackages({}, data) {
        return await api.get(`/v1/wallet/topup-packages`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     * @param {string} data.approve_flag
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getTopupApprovals({}, data) {
        return await api.get(`/v2/topup-approvals`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {number} data.bank_id
     * @param {number} data.topup_amount
     * @param {string} data.transfer_dt
     * @param {string} data.remark
     * @param {string} data.money_type //delivery, service
     * @param {string} data.slip_img
     */
    async postTopupApprovals({}, data) {
        return await api.post(`/v2/topup-approvals`, data);
    },

    //ANCHOR: Bank
    async getBank({}) {
        return await api.get(`/v1/banks`);
    },

    //ANCHOR: upload file
    /**
     * @param {Object} data
     * @param {string} data.account_id
     * @param {Object} data.formData
     * @param {string} data.formData.image_url
     * @param {string} data.formData.key_name
     *
     */
    async uploadFile({}, data) {
        return await api.post(
            `/v1/upload/${data.account_id}/file`,
            data.formData
        );
    },

    //ANCHOR: cod approval
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     * @param {string} data.approve_flag
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getCodApproval({}, data) {
        return await api.get(`/v1/wallet/cod-approvals`, { params: data });
    },

    /**
     * @param {Object} id
     */
    async getCodApprovalById({}, id) {
        return await api.get(`/v1/wallet/cod-approvals/${id}`);
    },

    /**
     * @param {Object} data
     * @param {string} data.id
     */
    async getCodApprovalShipmentOrders({}, id) {
        return await api.get(`/v1/wallet/cod-approvals/${id}/shipment-orders`);
    },

    //ANCHOR: other fee
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getOtherFees({}, data) {
        return await api.get(`/v1/other-fees`, { params: data });
    },

    /**
     * @param {number} id
     */
    async getOtherFeesById({}, id) {
        return await api.get(`/v1/other-fees/${id}`);
    },

    //ANCHOR: wallet transaction
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.payment_type
     * @param {string} data.search
     */
    async getReportWalletTransection({}, data) {
        return await api.get(`/v1/reports/wallet-transactions`, {
            params: data,
        });
    },

    //ANCHOR: Wallet Service Transaction
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.payment_type
     * @param {string} data.search
     */
    async getWalletServiceTransactions({}, data) {
        return await api.get(`/v1/wallet-service-transactions`, {
            params: data,
        });
    },

    //ANCHOR: Cod wallet Transactions
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.search
     */
    async getWalletServiceTransactionsReport({}, data) {
        return await api.get(`/v1/reports/cod-wallet-transactions`, {
            params: data,
        });
    },

    //ANCHOR: invoice approvals
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.status
     */
    async getInvoiceApprovals({}, data) {
        return await api.get(`/v1/invoice-approvals`, { params: data });
    },

    /**
     * @param {number} id
     */
    async getInvoiceApprovalsById({}, id) {
        return await api.get(`/v1/invoice-approvals/${id}`);
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {string} data.page
     *
     */
    async getInvoiceApprovable({}, data) {
        return await api.get(
            `/v1/invoice-approvals/${data.id}/invoice-approvable`,
            { params: data }
        );
    },

    /**
     * @param {number} id
     *
     */
    async exportInvoiceApprovable({}, id) {
        return await api.get(
            `/v1/invoice-approvals/${id}/invoice-approvable/export`,
            { responseType: "blob" }
        );
    },

    //ANCHOR: Credit Notes
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     * @param {string} data.status
     * @param {string} data.from_date
     * @param {string} data.to_date
     */
    async getCreditNotes({}, data) {
        return await api.get(`/v1/credit-notes`, { params: data });
    },

    /**
     * @param {number} id
     */
    async getCreditNoteById({}, id) {
        return await api.get(`/v1/credit-notes/${id}`);
    },

    /**
     * @param {number} id
     *
     */
    async getCreditNoteEtax({}, id) {
        return await api.get(`/v1/credit-notes/${id}/e-tax`);
    },

    /**
     * @param {number} id
     *
     */
    async exportCreditNoteDetail({}, id) {
        return await api.get(
            `/v1/credit-notes/${id}/credit-note-details/export`,
            { responseType: "blob" }
        );
    },

    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     */
    async getCreditNotesReport({}, data) {
        return await api.get(`/v1/reports/credit-notes`, { params: data });
    },

    //ANCHOR: Tax Invoices
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     * @param {string} data.sort
     * @param {string} data.search
     * @param {string} data.from_date
     * @param {string} data.to_date
     * @param {string} data.has_tax_doc
     * @param {string} data.invoice_type
     */
    async getTaxInvoices({}, data) {
        return await api.get(`/v1/tax-invoices`, { params: data });
    },

    /**
     * @param {number} id
     */
    async getTaxInvoiceById({}, id) {
        return await api.get(`/v1/tax-invoices/${id}`);
    },

    /**
     * @param {number} id
     */
    async getTaxInvoiceEtax({}, id) {
        return await api.get(`/v1/tax-invoices/${id}/e-tax`);
    },

    //ANCHOR: Api Key
    async getApiKey({}) {
        return await api.get(`/v1/api-key`);
    },

    async patchApiKey({}) {
        return await api.patch(`/v1/api-key`);
    },

    //ANCHOR: webhook markup
    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     */
    async getWebhookMarkup({}, data) {
        return await api.get(`/v1/webhook-markup`, { params: data });
    },

    /**
     * @param {Object} data
     * @param {string} data.page
     * @param {string} data.limit
     */
    async getWebhookMarkupById({}, data) {
        return await api.get(`/v1/webhook-markup/${data.id}`);
    },

    async getWebhookToken({}) {
        return await api.get(`/v1/webhook-markup/generate/token`);
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {string} data.formData.name
     * @param {string} data.formData.event
     * @param {string} data.formData.platform
     * @param {string} data.formData.webhook_url
     * @param {string} data.formData.token
     * @param {number} data.formData.active_flag
     */
    async createWebhookMarkup({}, data) {
        return await api.post(`/v1/webhook-markup`, data.formData);
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     * @param {string} data.formData.name
     * @param {string} data.formData.event
     * @param {string} data.formData.platform
     * @param {string} data.formData.webhook_url
     * @param {string} data.formData.token
     * @param {number} data.formData.active_flag
     */
    async editWebhookMarkup({}, data) {
        return await api.patch(`/v1/webhook-markup/${data.id}`, data.formData);
    },

    /**
     * @param {Object} data
     * @param {number} data.id
     */
    async deleteWebhookMarkup({}, id) {
        return await api.delete(`/v1/webhook-markup/${id}`);
    },

    //ANCHOR: packaging
    async getPackaging({}) {
        return await api.get(`/v1/packaging`);
    },
};
